import { type AxiosRequestConfig, type AxiosError, isAxiosError } from 'axios';

import ApiClient from 'helpers/base-api-client';
import { PAYMENT_REQUIRED } from 'constants/appEvents';
import EventEmitter from 'helpers/event-emitter';

/**
 * Represents the response structure for page data returned from the v3 REST API.
 */

export type V3ApiMetaData = {
  hasNextPage: boolean;
  nextPage: string;
  hasPreviousPage: boolean;
  previousPage: string;
};

/**
 * Represents the successful response data returned from the v3 REST API.
 * @template TData The type of data returned from the endpoint you are working with.
 * @example
 * type TestResponse = {
 *   test_strings: ['one', 'two', 'three'];
 * };
 *
 * type TestResponseData = V3ApiSuccessData<TestResponse>;
 */

export type V3ApiSuccessData<TData> = {
  data: TData;
  meta: V3ApiMetaData;
  errors: [];
};

/**
 * Represents the error response data returned from the API.
 * https://github.com/CompanyCam/Company-Cam-API/blob/master/adr/2023-12-04_REST-API-Guidelines.md#response-shapes
 */

export type V3ApiErrorData = {
  data: {};
  errors: {
    status: string;
    detail: string;
  }[];
  meta: {};
};

/**
 * Represents the entire API error returned from the v3 REST API.
 */
export type V3ApiError = AxiosError<V3ApiErrorData>;

const v3ApiClient = new ApiClient({ baseURL: '/v3/' });

/**
 * Handles client errors returned from the API.
 * @param {number} statusCode The status code returned from the API.
 *
 * TODO: Move this logic to the interceptor-level so it's always handled.
 */
const handleApiClientErrors = (statusCode: number) => {
  switch (statusCode) {
    case 401:
      window.location.replace('/users/sign_in');
      break;
    case 402:
      EventEmitter.trigger(PAYMENT_REQUIRED);
      break;
    default:
      break;
  }
};

/**
 * Makes a request to the v3 API. Use this as your queryFn if you are using react-query.
 * @param {AxiosRequestConfig} config The request configuration.
 * @returns {Promise<AxiosResponse<any, any>} The response from the API.
 */

export const request = async ({
  url,
  method = 'get',
  data,
  headers,
  params,
}: AxiosRequestConfig) => {
  try {
    return await v3ApiClient.client.request({
      url,
      method,
      data,
      headers,
      params,
    });
  } catch (error) {
    if (isAxiosError(error)) {
      handleApiClientErrors(error.response?.status);
      throw error;
    } else {
      throw new Error('Unknown error', error);
    }
  }
};
